import { useEffect, useRef, useState } from 'react';
import {
  CAMPOS_GESTION,
  PERFIL_GESTION_ID,
  capacidadGestion,
  saldoFactura,
  MAX_PDF_BYTES,
  type Organismo,
  type CapacidadOperador,
  type RegistroGestion,
  type TipoGestion,
  type DatosGestion,
  type GuardarGestion,
  type AuditEvent,
} from '@sumarios/shared';
import * as api from './api.js';

export type SeccionServicio =
  'resumen' | 'clientes' | 'contratos' | 'facturacion' | 'soporte' | 'auditoria' | 'equipo';
export const SECCIONES: { id: SeccionServicio; nombre: string; capacidad?: CapacidadOperador }[] = [
  { id: 'resumen', nombre: 'Resumen' },
  { id: 'equipo', nombre: 'Equipo IntiKipu', capacidad: 'operadores.gestionar' },
  { id: 'clientes', nombre: 'Clientes' },
  { id: 'contratos', nombre: 'Contratos', capacidad: 'finanzas.ver' },
  { id: 'facturacion', nombre: 'Facturación', capacidad: 'finanzas.ver' },
  { id: 'soporte', nombre: 'Soporte', capacidad: 'soporte.ver' },
  { id: 'auditoria', nombre: 'Auditoría', capacidad: 'auditoria.ver' },
];
const NOMBRES: Record<TipoGestion, string> = {
  contacto: 'Contactos',
  perfil: 'Ficha institucional',
  contrato: 'Contratos',
  factura: 'Facturas externas',
  pago: 'Pagos',
  reversion: 'Reversión de pago',
  ticket: 'Tickets',
  respuesta: 'Seguimiento',
};
const moneda = (valor: number) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(valor);
const hoy = () => new Date().toISOString().slice(0, 10);
function titulo(r: RegistroGestion): string {
  return String(
    r.datos.titulo ?? r.datos.numero ?? r.datos.nombre ?? r.datos.referencia ?? r.registroId,
  );
}
function estado(r: RegistroGestion): string {
  if (r.tipo === 'factura')
    return r.datos.estado === 'anulada'
      ? 'anulada'
      : saldoFactura(r) === 0
        ? 'pagada'
        : String(r.datos.vencimiento) < hoy()
          ? 'vencida'
          : (r.pagado ?? 0) > 0
            ? 'pago parcial'
            : 'pendiente';
  return String(r.datos.estado ?? 'registrado');
}
const mensaje = (e: unknown) =>
  e instanceof Error ? e.message : 'No se pudo completar la operación.';

export function GestionServicio({
  token,
  organismos,
  capacidades,
  seccion,
  orgId,
  onOrg,
  onClienteActualizado,
}: {
  token: string;
  organismos: Organismo[];
  capacidades: CapacidadOperador[];
  seccion: SeccionServicio;
  orgId: string | null;
  onOrg: (id: string) => void;
  onClienteActualizado: () => void;
}) {
  const [registros, setRegistros] = useState<RegistroGestion[]>([]);
  const [eventos, setEventos] = useState<AuditEvent[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [confirmacion, setConfirmacion] = useState('');
  const [revision, setRevision] = useState(0);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState('');
  const [detalle, setDetalle] = useState<RegistroGestion | null>(null);
  const [formulario, setFormulario] = useState<{
    tipo: TipoGestion;
    registro?: RegistroGestion | undefined;
    inicial?: DatosGestion;
  } | null>(null);
  const institucion = organismos.find((o) => o.orgId === orgId) ?? organismos[0];
  const orgSeleccionado = institucion?.orgId;
  const tipo: TipoGestion =
    seccion === 'clientes'
      ? 'contacto'
      : seccion === 'contratos'
        ? 'contrato'
        : seccion === 'facturacion'
          ? 'factura'
          : 'ticket';
  useEffect(() => {
    let vigente = true;
    setCargando(true);
    setError('');
    setDetalle(null);
    setFormulario(null);
    setBusqueda('');
    setFiltro('');
    const destinos = seccion === 'resumen' ? organismos : institucion ? [institucion] : [];
    const tipos: TipoGestion[] =
      seccion === 'resumen'
        ? ['contrato', 'factura', 'ticket']
        : seccion === 'clientes'
          ? ['contacto', 'perfil']
          : seccion === 'facturacion'
            ? ['factura', 'pago', 'reversion', 'contrato']
            : seccion === 'soporte'
              ? ['ticket', 'respuesta']
              : [tipo];
    void (async () => {
      try {
        if (seccion === 'auditoria') {
          const r = orgSeleccionado
            ? await api.auditGestion(token, orgSeleccionado)
            : { eventos: [] };
          if (vigente) setEventos(r.eventos);
        } else {
          const consultas = destinos.flatMap((o) =>
            tipos
              .filter((t) => capacidades.includes(capacidadGestion(t, false)))
              .map((t) => () => api.listarGestion(token, o.orgId, t)),
          );
          const todos: RegistroGestion[] = [];
          // Máximo cuatro lecturas simultáneas aunque haya muchas instituciones.
          for (let i = 0; i < consultas.length; i += 4) {
            const lote = await Promise.all(consultas.slice(i, i + 4).map((q) => q()));
            todos.push(...lote.flatMap((r) => r.registros));
          }
          if (vigente) setRegistros(todos);
        }
      } catch (e) {
        if (vigente) setError(mensaje(e));
      } finally {
        if (vigente) setCargando(false);
      }
    })();
    return () => {
      vigente = false;
    };
  }, [token, orgSeleccionado, seccion, revision, organismos, capacidades, tipo]);

  if (!organismos.length)
    return (
      <div className="tarjeta">
        <h2>Empieza por una institución</h2>
        <p>Da de alta una institución en Clientes para registrar contratos, facturas y soporte.</p>
      </div>
    );
  const principales = registros.filter((r) => r.tipo === tipo);
  const normalizar = (v: string) =>
    v
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  const visibles = principales.filter(
    (r) =>
      (!filtro || estado(r) === filtro) &&
      normalizar(Object.values(r.datos).join(' ')).includes(normalizar(busqueda)),
  );
  const alertaContratos = registros.filter(
    (r) =>
      r.tipo === 'contrato' &&
      r.datos.estado === 'vigente' &&
      String(r.datos.termino) <= new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  );
  const facturas = registros.filter((r) => r.tipo === 'factura');
  const tickets = registros.filter(
    (r) => r.tipo === 'ticket' && !['resuelto', 'cerrado'].includes(String(r.datos.estado)),
  );

  return (
    <section className="gestion-servicio col" aria-label="Gestión del servicio">
      <div className="tarjeta fila-entre">
        <div>
          <h2>{SECCIONES.find((s) => s.id === seccion)?.nombre}</h2>
          <p className="ayuda">
            {seccion === 'resumen'
              ? 'Vista de todas las instituciones según tus permisos.'
              : 'Registros internos de la institución seleccionada.'}
          </p>
        </div>
        {seccion !== 'resumen' && (
          <label>
            Institución
            <select
              value={orgSeleccionado}
              onChange={(e) => {
                setConfirmacion('');
                onOrg(e.target.value);
              }}
            >
              {organismos.map((o) => (
                <option key={o.orgId} value={o.orgId}>
                  {o.nombre}
                </option>
              ))}
            </select>
          </label>
        )}
        <button
          className="secundario"
          onClick={() => setRevision((r) => r + 1)}
          disabled={cargando}
        >
          Actualizar registros
        </button>
      </div>
      {error && (
        <p role="alert" className="error">
          {error} Los datos no se pudieron actualizar.
        </p>
      )}
      {confirmacion && (
        <p role="status" className="gestion-confirmacion">
          {confirmacion}
        </p>
      )}
      {cargando ? (
        <p role="status">Cargando registros…</p>
      ) : error ? null : seccion === 'resumen' ? (
        <>
          <div className="gestion-metricas">
            <div className="tarjeta">
              <span>Instituciones</span>
              <strong>{organismos.length}</strong>
            </div>
            {capacidades.includes('finanzas.ver') && (
              <>
                <div className="tarjeta">
                  <span>Saldo por cobrar</span>
                  <strong>{moneda(facturas.reduce((s, r) => s + saldoFactura(r), 0))}</strong>
                </div>
                <div className="tarjeta">
                  <span>Facturas vencidas</span>
                  <strong>{facturas.filter((r) => estado(r) === 'vencida').length}</strong>
                </div>
              </>
            )}
            {capacidades.includes('soporte.ver') && (
              <div className="tarjeta">
                <span>Tickets pendientes</span>
                <strong>{tickets.length}</strong>
              </div>
            )}
          </div>
          {capacidades.includes('finanzas.ver') && (
            <div className="tarjeta">
              <h3>Contratos vencidos o por vencer en 30 días</h3>
              {alertaContratos.length ? (
                <ul>
                  {alertaContratos.map((r) => (
                    <li key={r.registroId}>
                      {organismos.find((o) => o.orgId === r.orgId)?.nombre}: {titulo(r)} · término{' '}
                      {String(r.datos.termino)}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Sin contratos próximos a vencer.</p>
              )}
            </div>
          )}
          {capacidades.includes('soporte.ver') && (
            <div className="tarjeta">
              <h3>Soporte fuera de la fecha objetivo</h3>
              {tickets
                .filter((r) => r.datos.plazo && String(r.datos.plazo) < hoy())
                .map((r) => (
                  <p key={r.registroId}>
                    {organismos.find((o) => o.orgId === r.orgId)?.nombre}: {titulo(r)} ·{' '}
                    {String(r.datos.plazo)}
                  </p>
                ))}
              {!tickets.some((r) => r.datos.plazo && String(r.datos.plazo) < hoy()) && (
                <p>Sin tickets pendientes fuera de plazo.</p>
              )}
            </div>
          )}
        </>
      ) : seccion === 'auditoria' ? (
        <div className="tarjeta">
          <h3>Historial de cambios de la institución</h3>
          {eventos.length ? (
            eventos.map((e) => (
              <article key={e.logId} className="gestion-evento">
                <strong>{e.accion}</strong>
                <p>{e.detalle}</p>
                <small>
                  {e.actor.nombre} · {new Date(e.timestamp).toLocaleString('es-CL')}
                </small>
                <details>
                  <summary>Datos de trazabilidad</summary>
                  <code>
                    {e.logId}
                    <br />
                    {e.hashEncadenado}
                  </code>
                </details>
              </article>
            ))
          ) : (
            <p>Sin eventos registrados.</p>
          )}
        </div>
      ) : (
        <>
          {seccion === 'clientes' && institucion && (
            <div className="tarjeta col">
              <h3>Ficha institucional</h3>
              <p>
                {institucion.nombre} · RUT {institucion.rut} · {institucion.tipo}
              </p>
              <p>
                {institucion.comuna}, {institucion.region}
              </p>
              {capacidades.includes('clientes.gestionar') && (
                <button
                  className="secundario"
                  onClick={() =>
                    setFormulario({
                      tipo: 'perfil',
                      registro: registros.find((r) => r.tipo === 'perfil'),
                      inicial: {
                        nombre: institucion.nombre,
                        rut: institucion.rut,
                        tipo: institucion.tipo,
                        comuna: institucion.comuna,
                        region: institucion.region,
                      },
                    })
                  }
                >
                  Editar ficha institucional
                </button>
              )}
            </div>
          )}
          {seccion === 'facturacion' && (
            <p className="ayuda">
              Registro manual de facturas emitidas externamente y pagos recibidos, en CLP. Los
              registros no emiten documentos tributarios ni cambian el acceso al servicio.
            </p>
          )}
          {seccion === 'soporte' && (
            <p className="ayuda">
              Los tickets solicitados desde el portal permiten responder a la institución. Las notas
              internas y los tickets creados aquí se mantienen privados.
            </p>
          )}
          {seccion === 'contratos' && (
            <p className="ayuda">
              La vigencia contractual se gestiona por separado de la licencia de acceso.
            </p>
          )}
          <div className="tarjeta col">
            <div className="fila-entre">
              <h3>{NOMBRES[tipo]}</h3>
              {capacidades.includes(capacidadGestion(tipo, true)) && (
                <button
                  onClick={() => {
                    setDetalle(null);
                    setFormulario({ tipo });
                  }}
                >
                  {tipo === 'contacto'
                    ? 'Agregar contacto'
                    : tipo === 'contrato'
                      ? 'Registrar contrato'
                      : tipo === 'factura'
                        ? 'Registrar factura externa'
                        : 'Crear ticket'}
                </button>
              )}
            </div>
            <div className="fila">
              <label>
                Buscar registros
                <input
                  type="search"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </label>
              <label>
                Estado del registro
                <select value={filtro} onChange={(e) => setFiltro(e.target.value)}>
                  <option value="">Todos</option>
                  {[...new Set(principales.map(estado))].map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p role="status">
              {visibles.length} de {principales.length} registros
            </p>
            {visibles.length ? (
              <div
                className="tabla-scroll"
                role="region"
                aria-label="Registros de gestión"
                tabIndex={0}
              >
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>Registro</th>
                      <th>Estado</th>
                      <th>{tipo === 'factura' ? 'Saldo (CLP)' : 'Última actualización'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.map((r) => (
                      <tr key={r.registroId}>
                        <td>
                          <button
                            className="secundario"
                            onClick={() => {
                              setDetalle(r);
                              setFormulario(null);
                            }}
                          >
                            Ver {titulo(r)}
                          </button>
                        </td>
                        <td>
                          <span className="chip">{estado(r)}</span>
                        </td>
                        <td>
                          {tipo === 'factura'
                            ? moneda(saldoFactura(r))
                            : new Date(r.actualizadoEn).toLocaleDateString('es-CL')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No hay registros que coincidan.</p>
            )}
          </div>
          {detalle && (
            <div className="tarjeta col">
              <div className="fila-entre">
                <h3>{titulo(detalle)}</h3>
                {tipo === 'ticket' && detalle.canal === 'institucion' && (
                  <p className="chip">Solicitud de la institución</p>
                )}
                <button className="secundario" onClick={() => setDetalle(null)}>
                  Cerrar detalle
                </button>
              </div>
              <dl className="gestion-ficha">
                {CAMPOS_GESTION[detalle.tipo]
                  .filter((c) => detalle.datos[c.nombre] !== undefined)
                  .map((c) => (
                    <div key={c.nombre}>
                      <dt>{c.etiqueta}</dt>
                      <dd>
                        {c.tipo === 'dinero'
                          ? moneda(Number(detalle.datos[c.nombre]))
                          : c.referencia
                            ? titulo(
                                registros.find((r) => r.registroId === detalle.datos[c.nombre]) ??
                                  detalle,
                              )
                            : String(detalle.datos[c.nombre])}
                      </dd>
                    </div>
                  ))}
              </dl>
              <p className="ayuda">
                Versión {detalle.version} · actualizado{' '}
                {new Date(detalle.actualizadoEn).toLocaleString('es-CL')}
              </p>
              <HistorialRegistro
                key={`${detalle.registroId}-${detalle.version}`}
                token={token}
                registro={detalle}
              />
              {detalle.documentos.map((d, i) => (
                <Documento key={d.s3Key} token={token} registro={detalle} indice={i} />
              ))}
              {capacidades.includes(capacidadGestion(tipo, true)) && (
                <div className="fila">
                  <button
                    onClick={() => {
                      setFormulario({ tipo, registro: detalle });
                      setDetalle(null);
                    }}
                  >
                    Editar registro
                  </button>
                  {tipo === 'factura' && saldoFactura(detalle) > 0 && (
                    <button
                      onClick={() =>
                        setFormulario({ tipo: 'pago', inicial: { facturaId: detalle.registroId } })
                      }
                    >
                      Registrar pago
                    </button>
                  )}
                  {tipo === 'ticket' &&
                    detalle.canal === 'institucion' &&
                    detalle.datos.estado !== 'cerrado' && (
                      <button
                        onClick={() =>
                          setFormulario({
                            tipo: 'respuesta',
                            inicial: { ticketId: detalle.registroId, visibilidad: 'institucion' },
                          })
                        }
                      >
                        Responder a la institución
                      </button>
                    )}
                  {tipo === 'ticket' && detalle.datos.estado !== 'cerrado' && (
                    <button
                      onClick={() =>
                        setFormulario({
                          tipo: 'respuesta',
                          inicial: { ticketId: detalle.registroId, visibilidad: 'interna' },
                        })
                      }
                    >
                      Agregar nota interna
                    </button>
                  )}
                </div>
              )}
              {tipo === 'factura' && (
                <div>
                  <h4>Pagos registrados</h4>
                  {registros
                    .filter((r) => r.tipo === 'pago' && r.datos.facturaId === detalle.registroId)
                    .map((r) => (
                      <article key={r.registroId} className="gestion-evento">
                        {r.revertidoPor ? (
                          <p className="chip">Pago revertido</p>
                        ) : (
                          capacidades.includes('finanzas.gestionar') && (
                            <button
                              className="secundario"
                              onClick={() =>
                                setFormulario({
                                  tipo: 'reversion',
                                  inicial: { pagoId: r.registroId },
                                })
                              }
                            >
                              Revertir pago {String(r.datos.referencia)}
                            </button>
                          )
                        )}
                        <p>
                          {String(r.datos.fecha)} · {moneda(Number(r.datos.monto))} ·{' '}
                          {String(r.datos.referencia)}
                        </p>
                        {r.documentos.map((d, i) => (
                          <Documento key={d.s3Key} token={token} registro={r} indice={i} />
                        ))}
                      </article>
                    ))}
                  <p>
                    Total pagado: {moneda(detalle.pagado ?? 0)} · saldo:{' '}
                    {moneda(saldoFactura(detalle))}
                  </p>
                </div>
              )}
              {tipo === 'ticket' && (
                <div>
                  <h4>Seguimiento del ticket</h4>
                  {registros
                    .filter(
                      (r) => r.tipo === 'respuesta' && r.datos.ticketId === detalle.registroId,
                    )
                    .map((r) => (
                      <article key={r.registroId} className="gestion-evento">
                        <p className="gestion-texto">{String(r.datos.mensaje)}</p>
                        <small>
                          {new Date(r.creadoEn).toLocaleString('es-CL')} ·{' '}
                          {r.canal === 'institucion' ? 'Institución' : 'IntiKipu'} ·{' '}
                          {r.datos.visibilidad === 'institucion'
                            ? 'Visible para la institución'
                            : 'Nota interna'}
                        </small>
                        {r.documentos.map((d, i) => (
                          <Documento key={d.s3Key} token={token} registro={r} indice={i} />
                        ))}
                      </article>
                    ))}
                </div>
              )}
            </div>
          )}
          {formulario && orgSeleccionado && (
            <FormularioGestion
              key={`${orgSeleccionado}-${formulario.tipo}-${formulario.registro?.registroId ?? 'nuevo'}`}
              token={token}
              orgId={orgSeleccionado}
              {...formulario}
              registros={registros}
              onCancelar={() => setFormulario(null)}
              onGuardado={() => {
                setConfirmacion('Registro guardado y auditado.');
                setRevision((r) => r + 1);
                if (formulario.tipo === 'perfil') onClienteActualizado();
              }}
            />
          )}
        </>
      )}
    </section>
  );
}

function HistorialRegistro({ token, registro }: { token: string; registro: RegistroGestion }) {
  const [versiones, setVersiones] = useState<RegistroGestion[] | null>(null);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);
  return (
    <div>
      <button
        className="secundario"
        disabled={cargando}
        onClick={() => {
          setCargando(true);
          setError('');
          void api
            .historialGestion(token, registro)
            .then((r) => setVersiones(r.versiones))
            .catch((e) => setError(mensaje(e)))
            .finally(() => setCargando(false));
        }}
      >
        {cargando ? 'Cargando versiones…' : 'Ver versiones anteriores'}
      </button>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {versiones?.map((r) => (
        <details key={r.version} className="gestion-evento">
          <summary>
            Versión {r.version} · {new Date(r.actualizadoEn).toLocaleString('es-CL')}
          </summary>
          <dl className="gestion-ficha">
            {CAMPOS_GESTION[r.tipo]
              .filter((c) => r.datos[c.nombre] !== undefined)
              .map((c) => (
                <div key={c.nombre}>
                  <dt>{c.etiqueta}</dt>
                  <dd>
                    {c.tipo === 'dinero'
                      ? moneda(Number(r.datos[c.nombre]))
                      : String(r.datos[c.nombre])}
                  </dd>
                </div>
              ))}
          </dl>
          {r.tipo === 'factura' && <p>Total pagado en esta versión: {moneda(r.pagado ?? 0)}</p>}
          <p className="ayuda">
            Operador: {r.actualizadoPor} · {r.documentos.length} documentos asociados
          </p>
          {r.revertidoPor && <p>Pago revertido en esta versión.</p>}
        </details>
      ))}
    </div>
  );
}

function Documento({
  token,
  registro,
  indice,
}: {
  token: string;
  registro: RegistroGestion;
  indice: number;
}) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const documento = registro.documentos[indice]!;
  return (
    <div className="gestion-documento">
      <span>
        {documento.nombre} · {Math.ceil(documento.tamanoBytes / 1024)} KB
      </span>{' '}
      <button
        className="secundario"
        onClick={() => {
          setError('');
          void api
            .descargarGestion(token, registro, indice)
            .then((r) => setUrl(r.downloadUrl))
            .catch((e) => setError(mensaje(e)));
        }}
      >
        Preparar descarga
      </button>
      {url && (
        <a href={url} target="_blank" rel="noreferrer">
          Abrir PDF (enlace temporal)
        </a>
      )}
      <details>
        <summary>Hash del documento</summary>
        <code>{documento.sha256}</code>
      </details>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
    </div>
  );
}

function FormularioGestion({
  token,
  orgId,
  tipo,
  registro,
  inicial,
  registros,
  onCancelar,
  onGuardado,
}: {
  token: string;
  orgId: string;
  tipo: TipoGestion;
  registro?: RegistroGestion | undefined;
  inicial?: DatosGestion;
  registros: RegistroGestion[];
  onCancelar: () => void;
  onGuardado: () => void;
}) {
  const [datos, setDatos] = useState<DatosGestion>(
    () =>
      registro?.datos ?? {
        ...Object.fromEntries(
          CAMPOS_GESTION[tipo].filter((c) => c.opciones).map((c) => [c.nombre, c.opciones![0]!]),
        ),
        ...inicial,
      },
  );
  const [motivo, setMotivo] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const id = useRef(
    registro?.registroId ?? (tipo === 'perfil' ? PERFIL_GESTION_ID : crypto.randomUUID()),
  );
  const staging = useRef<{ archivo: File; key: string } | null>(null);
  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setGuardando(true);
    setError('');
    try {
      const entrada: GuardarGestion = {
        registroId: id.current,
        version: registro?.version ?? 0,
        datos,
        motivo,
      };
      if (archivo) {
        if (archivo.type !== 'application/pdf' || archivo.size <= 0 || archivo.size > MAX_PDF_BYTES)
          throw new Error('Adjunte un PDF de hasta 20 MB.');
        if (staging.current?.archivo !== archivo) {
          const r = await api.uploadGestion(token, orgId, tipo, id.current, archivo.size);
          await api.subirArchivo(r.uploadUrl, archivo);
          staging.current = { archivo, key: r.s3Key };
        }
        entrada.archivoStaging = staging.current!.key;
        entrada.nombreArchivo = archivo.name;
      }
      await api.guardarGestion(token, orgId, tipo, entrada);
      onGuardado();
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setGuardando(false);
    }
  }
  return (
    <form
      onSubmit={(e) => {
        void guardar(e);
      }}
      className="tarjeta col gestion-formulario"
      aria-label={`Formulario de ${NOMBRES[tipo]}`}
    >
      <h3>
        {registro ? 'Editar' : 'Registrar'} · {NOMBRES[tipo]}
      </h3>
      {(tipo === 'pago' || tipo === 'reversion') && (
        <p className="ayuda">
          El pago reduce el saldo y queda como registro inmutable. Comprueba el monto y la
          referencia antes de guardar.
        </p>
      )}
      {tipo === 'ticket' && registro?.canal === 'institucion' && (
        <p className="ayuda">
          El asunto, descripción, prioridad y estado son visibles para la institución. Use las notas
          internas para coordinación privada.
        </p>
      )}
      {tipo === 'respuesta' && (
        <p className="ayuda">
          Los mensajes son inmutables. Solo una respuesta marcada como visible aparece en el portal
          institucional.
        </p>
      )}
      <fieldset disabled={guardando} className="col">
        {CAMPOS_GESTION[tipo].map((c) => (
          <label key={c.nombre}>
            {c.etiqueta}
            {c.tipo === 'area' ? (
              <textarea
                value={datos[c.nombre] ?? ''}
                onChange={(e) => setDatos({ ...datos, [c.nombre]: e.target.value })}
                required={c.requerido}
                maxLength={4000}
                rows={3}
              />
            ) : c.opciones || c.referencia ? (
              <select
                value={datos[c.nombre] ?? ''}
                onChange={(e) => setDatos({ ...datos, [c.nombre]: e.target.value })}
                required={c.requerido}
              >
                <option value="">Seleccionar</option>
                {c.opciones
                  ? c.opciones.map((v) => (
                      <option key={v} value={v}>
                        {c.nombre === 'visibilidad'
                          ? v === 'institucion'
                            ? 'Visible para la institución'
                            : 'Nota interna'
                          : v.replaceAll('_', ' ')}
                      </option>
                    ))
                  : registros
                      .filter((r) => r.tipo === c.referencia)
                      .map((r) => (
                        <option key={r.registroId} value={r.registroId}>
                          {titulo(r)}
                          {r.tipo === 'factura' ? ` · saldo ${moneda(saldoFactura(r))}` : ''}
                        </option>
                      ))}
              </select>
            ) : (
              <input
                type={
                  c.tipo === 'dinero'
                    ? 'number'
                    : c.tipo === 'fecha'
                      ? 'date'
                      : c.tipo === 'email'
                        ? 'email'
                        : 'text'
                }
                value={datos[c.nombre] ?? ''}
                onChange={(e) =>
                  setDatos({
                    ...datos,
                    [c.nombre]:
                      c.tipo === 'dinero' && e.target.value !== ''
                        ? Number(e.target.value)
                        : e.target.value,
                  })
                }
                required={c.requerido}
                min={c.tipo === 'dinero' ? 1 : undefined}
                step={c.tipo === 'dinero' ? 1 : undefined}
                maxLength={250}
                readOnly={registro?.tipo === 'factura' && c.nombre === 'numero'}
              />
            )}
          </label>
        ))}
        <label>
          Adjuntar PDF (opcional, hasta 20 MB)
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
          />
        </label>
        <label>
          Motivo del registro o cambio
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            required
            maxLength={1000}
            rows={2}
          />
        </label>
        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        <div className="fila">
          <button type="submit">{guardando ? 'Guardando…' : 'Guardar registro'}</button>
          <button type="button" className="secundario" onClick={onCancelar}>
            Cancelar registro
          </button>
        </div>
      </fieldset>
    </form>
  );
}
